import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import DatePickerDMY from '../components/DatePickerDMY';
import {
  Plus, CurrencyCircleDollar, Buildings, ArrowClockwise, Trash,
  Warning, Bank, HandCoins, CheckCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_TYPES = [
  { key: 'management_company', label_en: 'Management Company', label_el: 'Εταιρεία Διαχείρισης' },
  { key: 'committee_member',   label_en: 'Committee Member',   label_el: 'Μέλος Επιτροπής' },
  { key: 'apartment_owner',    label_en: 'Apartment Owner',    label_el: 'Ιδιοκτήτης Διαμ.' },
  { key: 'third_party',        label_en: 'Third Party',        label_el: 'Τρίτος' },
  { key: 'other',              label_en: 'Other',              label_el: 'Άλλο' },
];
const sourceLabel = (key, language) => {
  const s = SOURCE_TYPES.find(x => x.key === key);
  return s ? (language === 'el' ? s.label_el : s.label_en) : key;
};
const fmtMoney = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const fmtDmy = (iso) => (iso && iso.length >= 10) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : (iso || '');

export default function AdvancesPage() {
  const { language } = useLanguage();
  const [buildings, setBuildings] = useState([]);
  const [buildingId, setBuildingId] = useState('');
  const [advances, setAdvances] = useState([]);
  const [summary, setSummary] = useState({ total_advanced: 0, total_repaid: 0, outstanding: 0, by_lender: [] });
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [advanceDialog, setAdvanceDialog] = useState(null);   // {mode:'create'|'edit', data}
  const [repaymentDialog, setRepaymentDialog] = useState(null); // {advance, form}
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {kind:'advance'|'repayment', id, title}
  // Phase 820.1 — Detail modal (repayments + linked expenses + audit log).
  const [detailDialog, setDetailDialog] = useState(null);   // {advance_id, data}
  const [linkExpDialog, setLinkExpDialog] = useState(null); // {advance_id, availableExpenses, selected}

  // Load buildings for the selector.
  useEffect(() => {
    axios.get(`${API}/buildings`).then(r => {
      const list = r.data || [];
      setBuildings(list);
      if (list.length > 0 && !buildingId) setBuildingId(list[0].building_id);
    }).catch(() => {});
  }, [buildingId]);

  const load = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([
        axios.get(`${API}/buildings/${buildingId}/advances`),
        axios.get(`${API}/buildings/${buildingId}/advances/summary`),
      ]);
      setAdvances(aRes.data?.advances || []);
      setSummary(sRes.data || { total_advanced: 0, total_repaid: 0, outstanding: 0, by_lender: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [buildingId]);
  useEffect(() => { load(); }, [load]);

  const openCreateAdvance = () => setAdvanceDialog({
    mode: 'create',
    data: {
      building_id: buildingId,
      date: new Date().toISOString().slice(0, 10),
      amount: '',
      source_type: 'management_company',
      source_name: '',
      paid_from: 'bank',
      reference: '',
      notes: '',
    },
  });
  const openEditAdvance = (adv) => setAdvanceDialog({ mode: 'edit', data: { ...adv } });

  const saveAdvance = async () => {
    const d = advanceDialog.data;
    if (!d.date) return toast.error(language === 'el' ? 'Απαιτείται ημερομηνία' : 'Date required');
    if (!(parseFloat(d.amount) > 0)) return toast.error(language === 'el' ? 'Απαιτείται ποσό > 0' : 'Amount > 0 required');
    if (!d.source_type) return toast.error(language === 'el' ? 'Επιλέξτε πηγή' : 'Pick a source');
    try {
      if (advanceDialog.mode === 'create') {
        await axios.post(`${API}/buildings/${buildingId}/advances`, {
          building_id: buildingId,
          date: d.date, amount: parseFloat(d.amount),
          source_type: d.source_type, source_name: d.source_name || '',
          paid_from: d.paid_from || 'bank', reference: d.reference || '', notes: d.notes || '',
        });
        toast.success(language === 'el' ? 'Δημιουργήθηκε' : 'Created');
      } else {
        await axios.put(`${API}/advances/${d.advance_id}`, {
          date: d.date, amount: parseFloat(d.amount),
          source_type: d.source_type, source_name: d.source_name || '',
          paid_from: d.paid_from || 'bank', reference: d.reference || '', notes: d.notes || '',
        });
        toast.success(language === 'el' ? 'Αποθηκεύτηκε' : 'Saved');
      }
      setAdvanceDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const openRepayment = (adv) => setRepaymentDialog({
    advance: adv,
    form: {
      date: new Date().toISOString().slice(0, 10),
      amount: adv.outstanding || '',
      paid_to: adv.source_name || '',
      reference: '', notes: '',
    },
  });
  const saveRepayment = async () => {
    const { advance, form } = repaymentDialog;
    if (!(parseFloat(form.amount) > 0)) return toast.error(language === 'el' ? 'Ποσό > 0' : 'Amount > 0 required');
    try {
      await axios.post(`${API}/advances/${advance.advance_id}/repayments`, {
        date: form.date, amount: parseFloat(form.amount),
        paid_to: form.paid_to || '', reference: form.reference || '', notes: form.notes || '',
      });
      toast.success(language === 'el' ? 'Καταχωρήθηκε' : 'Recorded');
      setRepaymentDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const doDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.kind === 'advance') {
        await axios.delete(`${API}/advances/${deleteConfirm.id}`);
      }
      toast.success(language === 'el' ? 'Διαγράφηκε' : 'Deleted');
      setDeleteConfirm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  // Phase 820.1 — Open the detail modal with repayments + linked expenses.
  const openDetail = async (advance_id) => {
    try {
      const r = await axios.get(`${API}/advances/${advance_id}/detail`);
      setDetailDialog({ advance_id, data: r.data });
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
  };

  // Phase 820.1 — Open the "Link Expenses" picker. Loads the building's
  // unpaid + partial expenses so the user can attach one or several.
  const openLinkExpenses = async (advance_id) => {
    try {
      const r = await axios.get(`${API}/buildings/${buildingId}/expenses`);
      const rows = (r.data || []).filter(x => (x.status || '').toLowerCase() !== 'paid');
      setLinkExpDialog({ advance_id, availableExpenses: rows, selected: new Set() });
    } catch (e) {
      // Fallback: allow entering IDs manually.
      setLinkExpDialog({ advance_id, availableExpenses: [], selected: new Set() });
    }
  };
  const saveLinkExpenses = async () => {
    if (!linkExpDialog) return;
    const ids = Array.from(linkExpDialog.selected);
    if (!ids.length) return toast.error(language === 'el' ? 'Επίλεξε τουλάχιστον 1' : 'Pick at least one');
    try {
      await axios.post(`${API}/advances/${linkExpDialog.advance_id}/link-expenses`, { expense_ids: ids });
      toast.success(language === 'el' ? 'Συνδέθηκαν' : 'Linked');
      const advId = linkExpDialog.advance_id;
      setLinkExpDialog(null);
      openDetail(advId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Link failed'); }
  };
  const unlinkExpense = async (expense_id) => {
    if (!detailDialog) return;
    try {
      await axios.post(`${API}/advances/${detailDialog.advance_id}/unlink-expense/${expense_id}`);
      toast.success(language === 'el' ? 'Αποσυνδέθηκε' : 'Unlinked');
      openDetail(detailDialog.advance_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Unlink failed'); }
  };

  const statusBadge = (s) => {
    const base = 'inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider';
    if (s === 'closed')  return { cls: `${base} bg-emerald-100 text-emerald-700`, label: language === 'el' ? 'Εξοφλήθηκε' : 'Closed' };
    if (s === 'partial') return { cls: `${base} bg-amber-100 text-amber-700`,     label: language === 'el' ? 'Μερικό' : 'Partial' };
    return                { cls: `${base} bg-red-100 text-red-700`,                label: language === 'el' ? 'Ανοιχτό' : 'Open' };
  };

  const totalCount = advances.length;
  const outstandingCount = useMemo(() => advances.filter(a => (a.status !== 'closed')).length, [advances]);

  return (
    <Layout>
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3" data-testid="advances-header">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>
              {language === 'el' ? 'Δάνεια / Προκαταβολές' : 'Loans / Advances'}
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">
              {language === 'el'
                ? 'Προσωρινή χρηματοδότηση προς την πολυκατοικία — παρακολούθηση οφειλών προς τρίτους'
                : 'Temporary funding provided to the building — track outstanding liabilities to third parties'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={buildingId}
              onChange={(e) => setBuildingId(e.target.value)}
              className="border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white min-w-[220px]"
              data-testid="advances-building-select"
            >
              {buildings.map(b => (<option key={b.building_id} value={b.building_id}>{b.name}</option>))}
            </select>
            <Button onClick={openCreateAdvance} className="bg-zinc-950 rounded-none" size="sm" data-testid="advances-add-btn">
              <Plus size={14} className="mr-1" /> {language === 'el' ? 'Νέα Προκαταβολή' : 'Add Advance'}
            </Button>
          </div>
        </div>

        {/* Hero cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="advances-hero">
          <div className="relative bg-gradient-to-br from-zinc-900 to-zinc-800 text-white overflow-hidden px-4 py-4 md:col-span-2">
            <span className="absolute left-0 top-0 bottom-0 w-1 bg-red-500" aria-hidden="true"></span>
            <div className="flex items-center gap-2 mb-1">
              <CurrencyCircleDollar size={16} className="text-red-300" weight="fill" />
              <span className="text-[10px] uppercase tracking-wider text-zinc-300 font-semibold">
                {language === 'el' ? 'Οφειλή Πολυκατοικίας' : 'Outstanding Advances'}
              </span>
            </div>
            <div className="text-4xl font-bold font-mono" style={{ fontVariantNumeric: 'tabular-nums' }} data-testid="advances-outstanding">
              {fmtMoney(summary.outstanding)}
            </div>
            <div className="text-xs text-zinc-400 mt-1 font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {language === 'el' ? 'Ποσό:' : 'Advanced:'} {fmtMoney(summary.total_advanced)}
              <span className="mx-1.5">·</span>
              {language === 'el' ? 'Εξοφλήθηκε:' : 'Repaid:'} {fmtMoney(summary.total_repaid)}
              <span className="mx-1.5">·</span>
              {outstandingCount}/{totalCount} {language === 'el' ? 'ανοιχτά' : 'open'}
            </div>
          </div>
          <div className="bg-white border border-zinc-200 px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <Buildings size={14} className="text-zinc-500" />
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                {language === 'el' ? 'Δανειστές' : 'Lenders'}
              </span>
            </div>
            <div className="text-2xl font-bold font-mono">{summary.by_lender.length}</div>
            <div className="text-[10px] text-zinc-500 mt-1">
              {summary.by_lender.slice(0, 3).map(l => l.source_name || sourceLabel(l.source_type, language)).join(' · ') || '—'}
            </div>
          </div>
        </div>

        {/* Per-lender breakdown */}
        {summary.by_lender.length > 0 && (
          <div className="bg-white border border-zinc-200" data-testid="advances-by-lender">
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200 bg-zinc-50 font-semibold">
              {language === 'el' ? 'Ανά Δανειστή' : 'By Lender'}
            </div>
            <div className="divide-y divide-zinc-100 text-sm">
              {summary.by_lender.map((l, i) => (
                <div key={i} className="grid grid-cols-4 items-center gap-3 px-3 py-2">
                  <div className="col-span-2">
                    <div className="font-semibold text-zinc-900 truncate">{l.source_name || '—'}</div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{sourceLabel(l.source_type, language)}</div>
                  </div>
                  <div className="text-right font-mono text-xs text-zinc-600" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtMoney(l.advanced)} / {fmtMoney(l.repaid)}
                  </div>
                  <div className="text-right font-mono font-bold text-red-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmtMoney(l.outstanding)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="advances-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200 bg-zinc-50">
                <th className="text-left font-semibold py-2.5 px-3 w-28">{language === 'el' ? 'Ημερομηνία' : 'Date'}</th>
                <th className="text-left font-semibold py-2.5 px-3">{language === 'el' ? 'Πηγή' : 'Source'}</th>
                <th className="text-left font-semibold py-2.5 px-3 w-32">{language === 'el' ? 'Reference' : 'Reference'}</th>
                <th className="text-right font-semibold py-2.5 px-3 w-24">{language === 'el' ? 'Ποσό' : 'Advanced'}</th>
                <th className="text-right font-semibold py-2.5 px-3 w-24">{language === 'el' ? 'Εξόφληση' : 'Repaid'}</th>
                <th className="text-right font-semibold py-2.5 px-3 w-28">{language === 'el' ? 'Υπόλοιπο' : 'Outstanding'}</th>
                <th className="text-left font-semibold py-2.5 px-3 w-24">{language === 'el' ? 'Κατάσταση' : 'Status'}</th>
                <th className="text-right font-semibold py-2.5 px-3 w-40">{language === 'el' ? 'Ενέργειες' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-10 text-center text-xs text-zinc-400">{language === 'el' ? 'Φόρτωση…' : 'Loading…'}</td></tr>
              ) : advances.length === 0 ? (
                <tr><td colSpan={8} className="py-10 text-center">
                  <CheckCircle size={24} className="mx-auto text-emerald-500 mb-1" weight="duotone" />
                  <div className="text-sm text-zinc-700 font-semibold">{language === 'el' ? 'Καμία προκαταβολή' : 'No advances yet'}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {language === 'el' ? 'Πάτησε "Νέα Προκαταβολή" για να ξεκινήσεις.' : 'Click "Add Advance" to get started.'}
                  </div>
                </td></tr>
              ) : (
                advances.map(a => {
                  const b = statusBadge(a.status);
                  return (
                    <tr key={a.advance_id} onClick={() => openDetail(a.advance_id)} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`advance-row-${a.advance_id}`}>
                      <td className="py-2.5 px-3 font-mono text-[13px] font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDmy(a.date)}</td>
                      <td className="py-2.5 px-3">
                        <div className="text-sm font-semibold text-zinc-900 truncate">{a.source_name || '—'}</div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{sourceLabel(a.source_type, language)}</div>
                        {a.notes && <div className="text-[11px] text-zinc-500 mt-0.5 truncate italic">{a.notes}</div>}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-xs text-zinc-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{a.reference || '—'}</td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.amount)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.repaid)}</td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-red-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.outstanding)}</td>
                      <td className="py-2.5 px-3"><span className={b.cls}>{b.label}</span></td>
                      <td className="py-2.5 px-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          {a.status !== 'closed' && (
                            <button type="button" onClick={() => openRepayment(a)} className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded" title={language === 'el' ? 'Καταχώρηση Εξόφλησης' : 'Add Repayment'} data-testid={`advance-repay-${a.advance_id}`}>
                              <ArrowClockwise size={14} weight="bold" />
                            </button>
                          )}
                          <button type="button" onClick={() => openEditAdvance(a)} className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded" title={language === 'el' ? 'Επεξεργασία' : 'Edit'} data-testid={`advance-edit-${a.advance_id}`}>
                            <HandCoins size={14} />
                          </button>
                          <button type="button" onClick={() => setDeleteConfirm({ kind: 'advance', id: a.advance_id, title: `${sourceLabel(a.source_type, language)} · ${fmtMoney(a.amount)}` })} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded" title={language === 'el' ? 'Διαγραφή' : 'Delete'} data-testid={`advance-delete-${a.advance_id}`}>
                            <Trash size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* -------- Advance dialog -------- */}
      <Dialog open={!!advanceDialog} onOpenChange={(v) => !v && setAdvanceDialog(null)}>
        <DialogContent className="sm:max-w-[560px] rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {advanceDialog?.mode === 'edit'
                ? (language === 'el' ? 'Επεξεργασία Προκαταβολής' : 'Edit Advance')
                : (language === 'el' ? 'Νέα Προκαταβολή' : 'New Advance')}
            </DialogTitle>
          </DialogHeader>
          {advanceDialog && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Ημερομηνία' : 'Date'}</Label>
                  <DatePickerDMY value={advanceDialog.data.date} onChange={v => setAdvanceDialog({ ...advanceDialog, data: { ...advanceDialog.data, date: v } })} />
                </div>
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Ποσό (€)' : 'Amount (€)'}</Label>
                  <Input type="number" step="0.01" value={advanceDialog.data.amount} onChange={e => setAdvanceDialog({ ...advanceDialog, data: { ...advanceDialog.data, amount: e.target.value } })} className="mt-1 rounded-none font-mono" data-testid="advance-amount" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{language === 'el' ? 'Τύπος Πηγής' : 'Source Type'}</Label>
                <select value={advanceDialog.data.source_type} onChange={e => setAdvanceDialog({ ...advanceDialog, data: { ...advanceDialog.data, source_type: e.target.value } })} className="w-full mt-1 border border-zinc-300 px-2 py-1.5 text-sm rounded-none bg-white" data-testid="advance-source-type">
                  {SOURCE_TYPES.map(s => (<option key={s.key} value={s.key}>{language === 'el' ? s.label_el : s.label_en}</option>))}
                </select>
              </div>
              <div>
                <Label className="text-xs">{language === 'el' ? 'Όνομα Πηγής' : 'Source Name'}</Label>
                <Input value={advanceDialog.data.source_name} onChange={e => setAdvanceDialog({ ...advanceDialog, data: { ...advanceDialog.data, source_name: e.target.value } })} className="mt-1 rounded-none" placeholder={language === 'el' ? 'π.χ. Pieri Management' : 'e.g. Pieri Management'} data-testid="advance-source-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Πληρώθηκε Από' : 'Paid From'}</Label>
                  <div className="flex gap-1.5 mt-1">
                    {[{ key: 'bank', Icon: Bank, label_en: 'Bank', label_el: 'Τράπεζα' }, { key: 'cash', Icon: HandCoins, label_en: 'Cash', label_el: 'Μετρητά' }].map(opt => {
                      const isActive = advanceDialog.data.paid_from === opt.key;
                      const Icon = opt.Icon;
                      return (
                        <button key={opt.key} type="button" onClick={() => setAdvanceDialog({ ...advanceDialog, data: { ...advanceDialog.data, paid_from: opt.key } })} className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs border ${isActive ? 'bg-zinc-900 text-white border-transparent' : 'border-zinc-200 hover:bg-zinc-50'}`}>
                          <Icon size={12} weight="bold" /> {language === 'el' ? opt.label_el : opt.label_en}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Reference' : 'Reference'}</Label>
                  <Input value={advanceDialog.data.reference} onChange={e => setAdvanceDialog({ ...advanceDialog, data: { ...advanceDialog.data, reference: e.target.value } })} className="mt-1 rounded-none font-mono" data-testid="advance-reference" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{language === 'el' ? 'Σημειώσεις' : 'Notes'}</Label>
                <Textarea rows={2} value={advanceDialog.data.notes} onChange={e => setAdvanceDialog({ ...advanceDialog, data: { ...advanceDialog.data, notes: e.target.value } })} className="mt-1 rounded-none text-sm" data-testid="advance-notes" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAdvanceDialog(null)} className="rounded-none">{language === 'el' ? 'Ακύρωση' : 'Cancel'}</Button>
            <Button onClick={saveAdvance} className="bg-zinc-950 rounded-none" data-testid="advance-save">{language === 'el' ? 'Αποθήκευση' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Repayment dialog -------- */}
      <Dialog open={!!repaymentDialog} onOpenChange={(v) => !v && setRepaymentDialog(null)}>
        <DialogContent className="sm:max-w-[480px] rounded-none">
          <DialogHeader>
            <DialogTitle>{language === 'el' ? 'Καταχώρηση Εξόφλησης' : 'Record Repayment'}</DialogTitle>
          </DialogHeader>
          {repaymentDialog && (
            <div className="space-y-3">
              <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs">
                <div className="font-semibold text-zinc-900">{repaymentDialog.advance.source_name || sourceLabel(repaymentDialog.advance.source_type, language)}</div>
                <div className="mt-0.5 text-zinc-600 font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {language === 'el' ? 'Υπόλοιπο:' : 'Outstanding:'} <b>{fmtMoney(repaymentDialog.advance.outstanding)}</b>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Ημερομηνία' : 'Date'}</Label>
                  <DatePickerDMY value={repaymentDialog.form.date} onChange={v => setRepaymentDialog({ ...repaymentDialog, form: { ...repaymentDialog.form, date: v } })} />
                </div>
                <div>
                  <Label className="text-xs">{language === 'el' ? 'Ποσό (€)' : 'Amount (€)'}</Label>
                  <Input type="number" step="0.01" value={repaymentDialog.form.amount} onChange={e => setRepaymentDialog({ ...repaymentDialog, form: { ...repaymentDialog.form, amount: e.target.value } })} className="mt-1 rounded-none font-mono" data-testid="repayment-amount" />
                </div>
              </div>
              <div>
                <Label className="text-xs">{language === 'el' ? 'Πληρώθηκε Σε' : 'Paid To'}</Label>
                <Input value={repaymentDialog.form.paid_to} onChange={e => setRepaymentDialog({ ...repaymentDialog, form: { ...repaymentDialog.form, paid_to: e.target.value } })} className="mt-1 rounded-none" data-testid="repayment-paidto" />
              </div>
              <div>
                <Label className="text-xs">Reference</Label>
                <Input value={repaymentDialog.form.reference} onChange={e => setRepaymentDialog({ ...repaymentDialog, form: { ...repaymentDialog.form, reference: e.target.value } })} className="mt-1 rounded-none font-mono" />
              </div>
              <div>
                <Label className="text-xs">{language === 'el' ? 'Σημειώσεις' : 'Notes'}</Label>
                <Textarea rows={2} value={repaymentDialog.form.notes} onChange={e => setRepaymentDialog({ ...repaymentDialog, form: { ...repaymentDialog.form, notes: e.target.value } })} className="mt-1 rounded-none text-sm" />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRepaymentDialog(null)} className="rounded-none">{language === 'el' ? 'Ακύρωση' : 'Cancel'}</Button>
            <Button onClick={saveRepayment} className="bg-emerald-700 hover:bg-emerald-800 rounded-none text-white" data-testid="repayment-save">
              <ArrowClockwise size={13} className="mr-1" />{language === 'el' ? 'Καταχώρηση' : 'Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Detail modal (repayments + linked expenses + audit) -------- */}
      <Dialog open={!!detailDialog} onOpenChange={(v) => !v && setDetailDialog(null)}>
        <DialogContent className="sm:max-w-[720px] rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CurrencyCircleDollar size={18} weight="bold" />
              {language === 'el' ? 'Λεπτομέρειες Προκαταβολής' : 'Advance Detail'}
            </DialogTitle>
          </DialogHeader>
          {detailDialog?.data && (() => {
            const a = detailDialog.data.advance;
            const reps = detailDialog.data.repayments || [];
            const linked = detailDialog.data.linked_expenses || [];
            const audit = detailDialog.data.audit_log || [];
            const b = statusBadge(a.status);
            return (
              <div className="space-y-4">
                {/* Header */}
                <div className="bg-zinc-50 border border-zinc-200 p-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-lg font-bold">{a.source_name || sourceLabel(a.source_type, language)}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{sourceLabel(a.source_type, language)}</div>
                    </div>
                    <span className={b.cls}>{b.label}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3 text-xs">
                    <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">{language === 'el' ? 'Ποσό' : 'Advanced'}</div><div className="text-base font-bold font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.amount)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">{language === 'el' ? 'Εξοφλήθηκε' : 'Repaid'}</div><div className="text-base font-bold font-mono text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.repaid)}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-zinc-500">{language === 'el' ? 'Υπόλοιπο' : 'Outstanding'}</div><div className="text-base font-bold font-mono text-red-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(a.outstanding)}</div></div>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-2 font-mono">
                    {fmtDmy(a.date)} · Ref: {a.reference || '—'} · {language === 'el' ? 'Δημιουργήθηκε από' : 'Created by'} {a.created_by_name || a.created_by || '—'}
                  </div>
                  {a.notes && <div className="text-xs mt-2 italic text-zinc-600 border-l-2 border-zinc-300 pl-2">{a.notes}</div>}
                </div>

                {/* Repayments */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      {language === 'el' ? 'Ιστορικό Εξόφλησης' : 'Repayment History'} ({reps.length})
                    </div>
                    {a.status !== 'closed' && (
                      <button type="button" onClick={() => { setDetailDialog(null); openRepayment(a); }} className="text-[11px] uppercase tracking-wider text-emerald-700 hover:underline" data-testid="detail-add-repayment">
                        + {language === 'el' ? 'Νέα Εξόφληση' : 'Add Repayment'}
                      </button>
                    )}
                  </div>
                  {reps.length === 0 ? (
                    <p className="text-xs text-zinc-400 italic">{language === 'el' ? 'Δεν έχουν καταχωρηθεί εξοφλήσεις.' : 'No repayments yet.'}</p>
                  ) : (
                    <div className="border border-zinc-200 divide-y divide-zinc-100">
                      {reps.map(r => (
                        <div key={r.repayment_id} className="grid grid-cols-4 items-center gap-2 px-3 py-1.5 text-sm">
                          <div className="font-mono text-xs">{fmtDmy(r.date)}</div>
                          <div className="text-xs col-span-2 truncate">{r.paid_to || '—'} {r.reference && <span className="text-zinc-400 ml-1 font-mono">· {r.reference}</span>}</div>
                          <div className="text-right font-mono font-semibold text-emerald-700" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(r.amount)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Linked expenses */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      {language === 'el' ? 'Συνδεδεμένα Expenses' : 'Linked Expenses'} ({linked.length})
                    </div>
                    <button type="button" onClick={() => openLinkExpenses(a.advance_id)} className="text-[11px] uppercase tracking-wider text-indigo-700 hover:underline" data-testid="detail-link-expenses">
                      + {language === 'el' ? 'Σύνδεση Expense' : 'Link Expense'}
                    </button>
                  </div>
                  {linked.length === 0 ? (
                    <p className="text-xs text-zinc-400 italic">
                      {language === 'el'
                        ? 'Δεν έχουν συνδεθεί Expenses. Χρησιμοποίησε το "Σύνδεση Expense" για να δηλώσεις ποιες πληρωμές κάλυψε αυτή η προκαταβολή.'
                        : 'No expenses linked. Use "Link Expense" to indicate which payments this advance covered.'}
                    </p>
                  ) : (
                    <div className="border border-zinc-200 divide-y divide-zinc-100">
                      {linked.map(e => (
                        <div key={e.expense_id} className="grid grid-cols-6 items-center gap-2 px-3 py-1.5 text-sm">
                          <div className="font-mono text-xs">{fmtDmy(e.date)}</div>
                          <div className="text-xs col-span-3 truncate">{e.vendor_name || e.notes || e.category || e.expense_id}</div>
                          <div className="text-right font-mono font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(e.amount)}</div>
                          <div className="text-right">
                            <button type="button" onClick={() => unlinkExpense(e.expense_id)} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-red-600" data-testid={`detail-unlink-${e.expense_id}`}>
                              × {language === 'el' ? 'Αφαίρεση' : 'Unlink'}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Audit trail */}
                {audit.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">
                      {language === 'el' ? 'Ιστορικό Ενεργειών' : 'Audit Trail'} ({audit.length})
                    </div>
                    <div className="border border-zinc-200 divide-y divide-zinc-100 max-h-40 overflow-y-auto">
                      {audit.slice().reverse().map((ev, i) => (
                        <div key={i} className="grid grid-cols-6 items-center gap-2 px-3 py-1 text-[11px]">
                          <div className="font-mono text-zinc-500">{ev.at ? ev.at.slice(0, 16).replace('T', ' ') : ''}</div>
                          <div className="text-xs col-span-2 uppercase tracking-wider text-zinc-700 font-semibold">{ev.action}</div>
                          <div className="text-xs col-span-3 text-zinc-500 truncate">
                            {ev.by_name || ev.by || '—'}
                            {ev.amount != null && <span className="ml-2 font-mono">{fmtMoney(ev.amount)}</span>}
                            {ev.expense_ids && <span className="ml-2 font-mono">{ev.expense_ids.join(', ')}</span>}
                            {ev.expense_id && <span className="ml-2 font-mono">{ev.expense_id}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialog(null)} className="rounded-none">{language === 'el' ? 'Κλείσιμο' : 'Close'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Link Expenses picker -------- */}
      <Dialog open={!!linkExpDialog} onOpenChange={(v) => !v && setLinkExpDialog(null)}>
        <DialogContent className="sm:max-w-[560px] rounded-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{language === 'el' ? 'Σύνδεση με Expenses' : 'Link Expenses'}</DialogTitle>
          </DialogHeader>
          {linkExpDialog && (
            <div className="space-y-2">
              <p className="text-xs text-zinc-500">
                {language === 'el'
                  ? 'Επίλεξε ένα ή περισσότερα Expenses για να δηλώσεις ότι αυτή η προκαταβολή κάλυψε το κόστος τους.'
                  : 'Pick one or more expenses that this advance funded.'}
              </p>
              {linkExpDialog.availableExpenses.length === 0 ? (
                <p className="text-sm text-zinc-500 italic py-6 text-center">
                  {language === 'el' ? 'Δεν βρέθηκαν διαθέσιμα Expenses για αυτό το κτίριο.' : 'No available expenses for this building.'}
                </p>
              ) : (
                <div className="border border-zinc-200 divide-y divide-zinc-100 max-h-72 overflow-y-auto">
                  {linkExpDialog.availableExpenses.map(e => {
                    const eid = e.expense_id;
                    const isChecked = linkExpDialog.selected.has(eid);
                    return (
                      <label key={eid} className="grid grid-cols-6 items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            const next = new Set(linkExpDialog.selected);
                            if (next.has(eid)) next.delete(eid); else next.add(eid);
                            setLinkExpDialog({ ...linkExpDialog, selected: next });
                          }}
                          data-testid={`link-exp-cb-${eid}`}
                        />
                        <div className="font-mono text-xs">{fmtDmy(e.date)}</div>
                        <div className="text-xs col-span-3 truncate">{e.vendor_name || e.notes || e.category || eid}</div>
                        <div className="text-right font-mono font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(e.amount)}</div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setLinkExpDialog(null)} className="rounded-none">{language === 'el' ? 'Ακύρωση' : 'Cancel'}</Button>
            <Button onClick={saveLinkExpenses} className="bg-indigo-700 hover:bg-indigo-800 rounded-none text-white" data-testid="link-exp-save">
              {language === 'el' ? 'Σύνδεση' : 'Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------- Delete confirm -------- */}
      <Dialog open={!!deleteConfirm} onOpenChange={(v) => !v && setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-[440px] rounded-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Warning size={18} weight="fill" />
              {language === 'el' ? 'Επιβεβαίωση Διαγραφής' : 'Confirm Deletion'}
            </DialogTitle>
          </DialogHeader>
          {deleteConfirm && (
            <div className="text-sm text-zinc-700 py-2">
              <p>{language === 'el' ? 'Θέλεις σίγουρα να διαγράψεις αυτή την προκαταβολή;' : 'Are you sure you want to delete this advance?'}</p>
              <p className="mt-2 text-zinc-500 font-mono text-xs border-l-2 border-zinc-200 pl-2 italic">
                &ldquo;{deleteConfirm.title}&rdquo;
              </p>
              <p className="mt-3 text-[11px] text-zinc-500">
                {language === 'el' ? 'Η ενέργεια δεν αναιρείται.' : 'This action cannot be undone.'}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-none">{language === 'el' ? 'Ακύρωση' : 'Cancel'}</Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 rounded-none text-white" data-testid="advance-delete-confirm">
              <Trash size={13} className="mr-1" />{language === 'el' ? 'Διαγραφή' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
